/**
 * The weekly progress report, mirroring the backend's typed columns rather than
 * a dynamic template. See apps/backend/src/progress-reports/dto.
 *
 * These names are the wire contract. The backend's ValidationPipe runs with
 * `forbidNonWhitelisted`, so an extra key here is a 400, not a silent ignore.
 */

export const SYMPTOM_FREQUENCIES = [
    'NEVER',
    'RARELY',
    'SOMETIMES',
    'FREQUENTLY',
    'ALWAYS',
] as const;

export type SymptomFrequency = (typeof SYMPTOM_FREQUENCIES)[number];

/** Visible labels. "Often" reads better than "Frequently" at a phone width. */
export const FREQUENCY_OPTIONS: readonly { value: SymptomFrequency; label: string }[] = [
    { value: 'NEVER', label: 'Never' },
    { value: 'RARELY', label: 'Rarely' },
    { value: 'SOMETIMES', label: 'Sometimes' },
    { value: 'FREQUENTLY', label: 'Often' },
    { value: 'ALWAYS', label: 'Always' },
];

/** The 11 physical wellness rows, in the order the paper form asks them. */
export const PHYSICAL_ROWS = [
    { name: 'troubleFallingAsleep', legend: 'Trouble falling asleep' },
    { name: 'wakeDuringNight', legend: 'Waking during the night' },
    { name: 'needCaffeineToGetGoing', legend: 'Needing caffeine to get going' },
    { name: 'mentalFog', legend: 'Mental fog' },
    { name: 'sinusitisOrPostNasalDrip', legend: 'Sinusitis or post-nasal drip' },
    { name: 'heartburnOrAcidReflux', legend: 'Heartburn or acid reflux' },
    { name: 'bloatingOrFlatulence', legend: 'Bloating or flatulence' },
    { name: 'constipation', legend: 'Constipation' },
    { name: 'diarrhoea', legend: 'Diarrhoea' },
    { name: 'headachesOrMigraines', legend: 'Headaches or migraines' },
    { name: 'otherPain', legend: 'Other pain' },
] as const;

/** The 8 emotion rows. */
export const EMOTION_ROWS = [
    { name: 'anxious', legend: 'Anxious' },
    { name: 'depressed', legend: 'Depressed' },
    { name: 'stressed', legend: 'Stressed' },
    { name: 'content', legend: 'Content' },
    { name: 'peacefulAndCalm', legend: 'Peaceful and calm' },
    { name: 'compassionWithMyself', legend: 'Compassionate with myself' },
    { name: 'thankfulForMyLife', legend: 'Thankful for my life' },
    { name: 'optimisticAboutMyProgress', legend: 'Optimistic about my progress' },
] as const;

export type PhysicalField = (typeof PHYSICAL_ROWS)[number]['name'];
export type EmotionField = (typeof EMOTION_ROWS)[number]['name'];

/**
 * The submit payload, which is also the edit payload: the backend keys on
 * (client, week) and updates the row if this week's report is already in.
 *
 * `periodStart` is derived server-side from the date and must never be sent —
 * choosing it would mean choosing which week to overwrite. `clientId` comes
 * from the access token for the same reason.
 *
 * `weightKg` is omitted entirely when the client cannot weigh themselves, so
 * the stored value is null rather than zero.
 */
export type CreateProgressReportRequest = {
    reportDate?: string;
    energyLevel: number;
    sleepQuality: number;
    painDescription: string;
    painLevel: number;
    negativeFoodReactions: string;
    weightKg?: number;
    commitmentLevel: number;
    questionsOrRemarks?: string;
} & Record<PhysicalField | EmotionField, SymptomFrequency>;

/**
 * A stored report as it comes back from the API.
 *
 * Re-exported from the shared package rather than declared twice: the progress
 * view (#232) and the coach's comparison sheet both do arithmetic on this shape
 * — notably `weightKg`, which is a STRING ("159.7") because the column is a
 * Prisma Decimal and Decimal serialises to JSON as a string to keep its
 * precision. Parse it before doing arithmetic; never assume it is numeric.
 *
 * The request type above stays local. It is what this app sends, not what the
 * platform stores, and it is the client form's contract alone.
 */
export type { ProgressReport } from '@light2live/shared-types';
